<template>
  <div class="duration-range-container">
    <div class="duration-inputs-wrapper">
      <div class="duration-input-group">
        <label for="min-duration-input">Min:</label>
        <input
          type="text"
          id="min-duration-input"
          class="duration-input"
          v-model="minInputValue"
          @input="restrictInput($event, 'min')"
          @focus="handleMinFocus"
          @blur="validateMinInput"
          placeholder="0:00"
        />
      </div>
      <div class="duration-input-group">
        <label for="max-duration-input">Max:</label>
        <input
          type="text"
          id="max-duration-input"
          class="duration-input"
          v-model="maxInputValue"
          @input="restrictInput($event, 'max')"
          @focus="handleMaxFocus"
          @blur="validateMaxInput"
          :placeholder="maxPlaceholder"
        />
      </div>
    </div>
    <div class="duration-warning">
      <small v-if="warningMessage">{{ warningMessage }}</small>
    </div>
  </div>
</template>

<script>
import Vue from "vue";
import { playerService } from "../player-service";

export default Vue.component("duration-range", {
  props: {
    value: {
      type: Array,
      default: () => [0, 0],
    },
  },

  data() {
    return {
      internalMin: this.value[0],
      internalMax: this.value[1],
      max: 2000,
      min: 0,
      minInputValue: "",
      maxInputValue: "",
      maxPlaceholder: "0:00",
      warningMessage: "",
    };
  },

  mounted() {
    let ctx = this;

    // Fetch the maximum track duration
    $.ajax({
      method: "GET",
      url: `/api/tracks-max-length`,
      headers: {
        "X-CSRF-TOKEN": $('meta[name="csrf-token"]').attr("content"),
      },
      cache: false,
    }).done(function (length) {
      ctx.max = parseInt(length);
      ctx.internalMax = ctx.max;
      ctx.maxPlaceholder = ctx.formatDuration(ctx.max);

      playerService.$emit("filtering-max-duration-change", ctx.max);
    });
  },

  watch: {
    internalMin(value) {
      // Only emit if the range is valid (min <= max)
      if (value <= this.internalMax) {
        this.$emit("input", [value, this.internalMax]);
      }
    },

    internalMax(value) {
      // Only emit if the range is valid (max >= min)
      if (value >= this.internalMin) {
        this.$emit("input", [this.internalMin, value]);
      }
    },

    value(newValue) {
      // Update from parent only if values changed
      if (this.internalMin !== newValue[0]) {
        this.internalMin = newValue[0];
        this.minInputValue =
          newValue[0] === this.min ? "" : this.formatDuration(newValue[0]);
      }

      if (this.internalMax !== newValue[1]) {
        this.internalMax = newValue[1];
        this.maxInputValue =
          newValue[1] === this.max ? "" : this.formatDuration(newValue[1]);
      }

      this.warningMessage = "";
    },
  },

  methods: {
    formatDuration(seconds) {
      if (!seconds || typeof seconds !== "number") {
        return "0:00";
      }

      seconds = parseInt(seconds);
      const minutes = Math.floor(seconds / 60);
      let secs = seconds % 60;

      if (secs < 10) {
        secs = "0" + secs;
      }

      return minutes + ":" + secs;
    },

    parseDuration(timeString) {
      timeString = timeString.trim();

      // Handle pure numbers (e.g., "120", "315")
      if (/^\d+$/.test(timeString)) {
        const totalSeconds = parseInt(timeString);

        if (totalSeconds <= 59) {
          // 0-59: treated as seconds
          return totalSeconds;
        } else if (totalSeconds >= 100) {
          // ≥100: treated as MMSS format
          const minutes = Math.floor(totalSeconds / 100);
          const seconds = totalSeconds % 100;

          if (seconds > 59) {
            return null; // Invalid seconds component
          }

          return minutes * 60 + seconds;
        } else {
          // 60-99: treated as seconds
          return totalSeconds;
        }
      }

      // Handle MM:SS format
      const parts = timeString.split(":");

      if (parts.length !== 2) {
        return null;
      }

      const minutes = parseInt(parts[0]);
      const seconds = parseInt(parts[1]);

      if (isNaN(minutes) || isNaN(seconds) || seconds < 0 || seconds > 59) {
        return null;
      }

      return minutes * 60 + seconds;
    },

    handleMinFocus() {
      // Just clear the warning, don't change the input value
      this.warningMessage = "";
    },

    restrictInput(event, field) {
      // Only allow numbers and colon
      const value = event.target.value;
      const sanitized = value.replace(/[^0-9:]/g, "");

      if (value !== sanitized) {
        if (field === "min") {
          this.minInputValue = sanitized;
        } else {
          this.maxInputValue = sanitized;
        }
      }
    },

    validateMinInput() {
      const seconds = this.parseDuration(this.minInputValue);

      if (seconds !== null && seconds >= this.min && seconds <= this.max) {
        this.internalMin = seconds;
        this.minInputValue = this.formatDuration(seconds);

        // Check if min exceeds max and show warning
        if (this.internalMin > this.internalMax) {
          this.warningMessage = "Minimum duration cannot exceed maximum";
        } else {
          this.warningMessage = "";
        }
      } else if (this.minInputValue.trim() === "") {
        this.internalMin = this.min;
        this.minInputValue = "";
        this.warningMessage = "";
      } else if (seconds !== null && seconds > this.max) {
        // Exceeds maximum track duration - set to max
        this.internalMin = this.max;
        this.minInputValue = this.formatDuration(this.max);
        this.warningMessage = "";
      } else if (seconds !== null && seconds > this.internalMax) {
        // Min exceeds the user-set max
        this.minInputValue = this.formatDuration(seconds);
        this.warningMessage = "Minimum duration cannot exceed maximum";
      } else {
        // Invalid input format
        this.warningMessage = "Invalid duration";
      }
    },

    handleMaxFocus() {
      // Just clear the warning, don't change the input value
      this.warningMessage = "";
    },

    validateMaxInput() {
      const seconds = this.parseDuration(this.maxInputValue);

      if (seconds !== null && seconds >= this.min && seconds <= this.max) {
        this.internalMax = seconds;
        this.maxInputValue = this.formatDuration(seconds);

        // Check if max is less than min and show warning
        if (this.internalMax < this.internalMin) {
          this.warningMessage = "Maximum duration cannot be less than minimum";
        } else {
          this.warningMessage = "";
        }
      } else if (this.maxInputValue.trim() === "") {
        this.internalMax = this.max;
        this.maxInputValue = "";
        this.warningMessage = "";
      } else if (seconds !== null && seconds > this.max) {
        // Exceeds maximum track duration - set to max
        this.internalMax = this.max;
        this.maxInputValue = this.formatDuration(this.max);
        this.warningMessage = "";
      } else {
        // Invalid input format
        this.warningMessage = "Invalid duration";
      }
    },
  },
});
</script>
